import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  IconButton,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from '../../components/UserDialog/ConfirmDialog';
import axios from 'axios';

import { AgGridReact } from 'ag-grid-react';
import {
  ClientSideRowModelModule,
  ClipboardModule,
  RichSelectModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
  CheckboxEditorModule,
  RenderApiModule,
  ModuleRegistry,
} from 'ag-grid-enterprise';
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  RichSelectModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
  CheckboxEditorModule,
  RenderApiModule,
]);

export default function UsersList() {
  const gridRef = useRef(null);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [groupOptions, setGroupOptions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    id: '',
    name: '',
    userName: '',
    email: '',
    phone: '',
    lastLogin: '',
    groupId: '',
    groupOption: null,
  });

  // ── New state for ConfirmDialog ──
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchGroupsMetadata();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/Admin/users');
      setRowData(data);
    } catch (err) {
      console.error('Failed to fetch users', err);
      setRowData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupsMetadata = async () => {
    try {
      const { data } = await axios.get('/api/Admin/user-groups-basic');
      const mapped = data.map((g) => ({
        id: g.groupId,
        name: g.name,
      }));
      setGroupOptions(mapped);
    } catch (err) {
      console.error('Failed to fetch groups metadata', err);
      setGroupOptions([]);
    }
  };

  // ── Instead of deleting immediately, open the confirm dialog ──
  const openConfirm = (userId) => {
    setUserToDelete(userId);
    setConfirmOpen(true);
  };

  // ── Close without deleting ──
  const closeConfirm = () => {
    setConfirmOpen(false);
    setUserToDelete(null);
  };

  // ── After user clicks “Yes,” actually delete ──
  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/Admin/user/${userToDelete}`);
      await fetchUsers();
    } catch (err) {
      console.error(`Failed to delete user ${userToDelete}`, err);
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
      setUserToDelete(null);
    }
  };

  const columnDefs = useMemo(
    () => [
      { headerName: 'ID', field: 'id', hide: true },
      {
        headerName: 'Actions',
        field: 'actions',
        filter: false,
        minWidth: 120,
        cellRenderer: (params) => (
          <Box>
            <IconButton size="small" onClick={() => handleEditClick(params.data)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => openConfirm(params.data.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
      {
        headerName: 'Name',
        field: 'name',
        flex: 1,
        sort: 'asc',
      },
      { headerName: 'Username', field: 'userName', flex: 1 },
      { headerName: 'Email', field: 'email', flex: 1.5 },
      { headerName: 'Phone', field: 'phone', flex: 1 },
      { headerName: 'Last Login', field: 'lastLogin', flex: 1 },
      {
        headerName: 'Group',
        field: 'groupId',
        flex: 1,
        valueFormatter: (params) => {
          const gid = params.value;
          const match = groupOptions.find((g) => g.id === gid);
          return match ? match.name : gid;
        },
      },
    ],
    [groupOptions]
  );

  const defaultColDef = useMemo(
    () => ({
      filter: true,
    }),
    []
  );

  const openAddDialog = () => {
    setIsEditMode(false);
    setCurrentUser({
      id: '',
      name: '',
      userName: '',
      email: '',
      phone: '',
      lastLogin: '',
      groupId: '',
      groupOption: null,
    });
    setDialogOpen(true);
  };

  const handleEditClick = (user) => {
    setIsEditMode(true);
    const matchedOption = groupOptions.find((g) => g.id === user.groupId) || null;
    setCurrentUser({
      id: user.id,
      name: user.name,
      userName: user.userName,
      email: user.email,
      phone: user.phone,
      lastLogin: user.lastLogin,
      groupId: user.groupId || '',
      groupOption: matchedOption,
    });
    setDialogOpen(true);
  };

  const handleSaveUser = async () => {
    const payload = {
      id: currentUser.id,
      name: currentUser.name,
      userName: currentUser.userName,
      email: currentUser.email,
      phone: currentUser.phone,
      groupId: currentUser.groupId,
    };

    try {
      if (isEditMode) {
        await axios.put(`/api/Admin/user/${currentUser.id}`, payload);
      } else {
        await axios.post('/api/Admin/user', payload);
      }
      await fetchUsers();
      setDialogOpen(false);
    } catch (err) {
      console.error('Failed to save user', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Button variant="contained" onClick={openAddDialog}>
          Add User
        </Button>
      </Box>

      <div style={{ height: '74vh', width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection={{
            mode: 'multiRow',
            checkboxes: false,
            headerCheckbox: false,
            suppressRowClickSelection: true,
          }}
          animateRows
          loading={loading}
        />
      </div>

      {/* ── Add/Edit User Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Name" name="name" value={currentUser.name} onChange={handleInputChange} fullWidth />
            <TextField
              label="Username"
              name="userName"
              value={currentUser.userName}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField label="Email" name="email" value={currentUser.email} onChange={handleInputChange} fullWidth />
            <TextField label="Phone" name="phone" value={currentUser.phone} onChange={handleInputChange} fullWidth />
            <Autocomplete
              options={groupOptions}
              getOptionLabel={(opt) => opt.name}
              value={currentUser.groupOption}
              onChange={(e, newVal) => {
                setCurrentUser((prev) => ({
                  ...prev,
                  groupId: newVal ? newVal.id : '',
                  groupOption: newVal,
                }));
              }}
              renderInput={(params) => <TextField {...params} label="Group" />}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveUser} disabled={!currentUser.name.trim()}>
            {isEditMode ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── ConfirmDialog for delete ── */}
      <ConfirmDialog
        open={confirmOpen}
        message="Are you sure you want to delete this user?"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onClose={closeConfirm}
        onCancelClick={closeConfirm}
        showCancel={true}
        showNo={false}
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
      />
    </Box>
  );
}
